// src/constants/Settings.constants.ts

import type {
  AppSettings,
  EmailData,
  HospitalSettingsNotificationPreferences,
  NotificationPreferences,
  PasswordData,
} from "../types/Settings.types";

export const LANGUAGE_OPTIONS = [
  { value: "EN", label: "English" },
] as const;

export const TIMEZONE_OPTIONS = [
  { value: "Africa/Lagos", label: "West Africa Time (Lagos)" },
  { value: "UTC", label: "UTC" },
] as const;

export const GREETING_STYLE_OPTIONS = [
  { value: "formal", label: "Formal" },
  { value: "casual", label: "Casual" },
  { value: "none", label: "No greeting" },
] as const;


export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  global: {
    emailNotifications: true,
    smsNotifications: true,
    systemUpdates: true,
  },

  hospital: {
    newAppointments: true,
    appointmentChanges: true,
    consultationReminders: true,
    newPatientAssignments: true,
    urgentPatientAlerts: true,
    testResultUpdates: true,
    staffAccountUpdates: true,
    facilityProfileUpdates: true,
  },

  laboratory: {
    newTestRequests: true,
    urgentTests: true,
    completedTests: true,
    sampleCollected: true,
    qualityAlerts: true,
    equipmentAlerts: true,
    lowInventory: true,
  },

  insurance: {
    newClaims: true,
    urgentPreAuths: true,
    completedVerifications: true,
    claimUpdates: true,
    enrollmentUpdates: true,
  },
};


export const DEFAULT_HOSPITAL_NOTIFICATION_PREFERENCES: HospitalSettingsNotificationPreferences =
  {
    emailNotifications: true,
    smsNotifications: true,
    systemUpdates: true,

    newAppointments: true,
    appointmentChanges: true,
    consultationReminders: true,

    newPatientAssignments: true,
    urgentPatientAlerts: true,
    testResultUpdates: true,

    staffAccountUpdates: true,
    facilityProfileUpdates: true,
  };


export const DEFAULT_SETTINGS: AppSettings = {
  email: "",

  notifications: {
    ...DEFAULT_HOSPITAL_NOTIFICATION_PREFERENCES,
  },

  language: "EN",

  timezone: "Africa/Lagos",

  workingHours: {
    start: "09:00",
    end: "17:00",
  },

  twoFactorAuth: false,

  greetingStyle: "casual",
};


export const PASSWORD_REQUIREMENTS = {
  minLength: 8,
  requireUppercase: true,
  requireLowercase: true,
  requireNumber: true,
  requireSpecialChar: true,
} as const;


export const EMAIL_REGEX =
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/;


export const DEFAULT_PASSWORD_DATA: PasswordData = {
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
};


export const DEFAULT_EMAIL_DATA: EmailData = {
  currentEmail: "",
  newEmail: "",
  confirmEmail: "",
  password: "",
};


export const styles = {
  card:
    "overflow-hidden rounded-xl border border-[#E4EFED] bg-white",

  sectionTitle:
    "text-base font-semibold text-[#17313A]",

  sectionDescription:
    "mt-1 text-sm leading-5 text-[#65777C]",

  settingRow:
    "flex items-center justify-between gap-4 border-t border-[#E4EFED] px-6 py-4",

  settingItem:
    "group flex w-full items-center justify-between gap-4 border-t border-[#E4EFED] px-6 py-4 text-left transition-colors hover:bg-[#F8FAF9] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#159A92]/25",

  settingLabel:
    "text-sm font-medium text-[#17313A]",

  settingDescription:
    "mt-1 text-sm leading-5 text-[#65777C]",

  label:
    "mb-2 block text-sm font-medium text-[#17313A]",

  input:
    "w-full rounded-lg border border-[#D6E4E2] bg-white focus:border-[#159A92] focus:ring-[#159A92]",

  button: {
    primary:
      "inline-flex items-center justify-center rounded-lg bg-[#159A92] px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#117E78] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#159A92]/25 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",

    outline:
      "inline-flex items-center justify-center rounded-lg border border-[#D6E4E2] bg-white px-5 py-2.5 text-sm font-medium text-[#17313A] transition-colors hover:bg-[#F8FAF9] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#159A92]/25 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  },
} as const;


export const STORAGE_KEY =
  "gluviacare.hospital-settings";

export const NOTIFICATIONS_STORAGE_KEY =
  "gluviacare.notifications";

export const NOTIFICATION_SETTINGS_STORAGE_KEY =
  "gluviacare.notification-settings";

export const NOTIFICATION_PREFERENCES_STORAGE_KEY =
  "gluviacare.notification-preferences";
